import React, { useMemo, useState } from 'react'
import { Loader2, Search, Download } from 'lucide-react'
import { Input } from '@/race-tracker/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/race-tracker/components/ui/select'
import { Button } from '@/race-tracker/components/ui/button'
import StationShareCard from '@/big-political-list/components/stations/StationShareCard'
import { exportStationShareToExcel } from '@/big-political-list/lib/stationShareExport'
import {
  useStationShareMarkets,
  useStationShareEntries,
  useStationShareNotes,
} from '@/big-political-list/hooks/useBplData'
import BplQueryError from '@/big-political-list/components/shared/BplQueryError'
import { getBplUserName } from '@/big-political-list/lib/bplUser'

function MetricCard({ label, value, highlight, highlightColor, onClick, active }) {
  const colorClass = highlight
    ? highlightColor === 'emerald' ? 'text-emerald-600' : 'text-amber-600'
    : 'text-foreground'
  return (
    <div
      onClick={onClick}
      className={`rounded-lg border bg-card px-6 py-5 transition-colors ${onClick ? 'cursor-pointer hover:bg-accent' : ''} ${active ? 'ring-2 ring-emerald-500 bg-emerald-50' : ''}`}
    >
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={`text-4xl font-bold mt-1 ${colorClass}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {active && <p className="text-xs text-emerald-600 mt-1 font-medium">Filtered — click to clear</p>}
    </div>
  )
}

export default function StationShare({ canWrite = true, stations = [], user, profile }) {
  const { data: markets = [], isLoading: loadingMarkets, isFetching: fetchingMarkets, isError: marketsError, error: marketsErr, refetch: refetchMarkets } = useStationShareMarkets()
  const { data: entries = [], isLoading: loadingEntries, isFetching: fetchingEntries } = useStationShareEntries()
  const { data: notes = [], isLoading: loadingNotes, isFetching: fetchingNotes } = useStationShareNotes()

  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState('all')
  const [dealsOnly, setDealsOnly] = useState(false)
  const [exporting, setExporting] = useState(false)

  const userName = useMemo(() => getBplUserName(profile, user), [profile, user])

  const entriesMap = useMemo(() => {
    const map = {}
    for (const e of entries) {
      if (!map[e.market_id]) map[e.market_id] = []
      map[e.market_id].push(e)
    }
    return map
  }, [entries])

  const notesMap = useMemo(() => {
    const map = {}
    for (const n of notes) {
      if (!map[n.market_id]) map[n.market_id] = []
      map[n.market_id].push(n)
    }
    return map
  }, [notes])

  const metrics = useMemo(() => {
    const dealCount = markets.filter(m => (entriesMap[m.id] || []).some(e => e.deal)).length
    return { markets: markets.length, stations: entries.length, deal: dealCount }
  }, [markets, entries, entriesMap])

  const stateOptions = useMemo(() => {
    const s = new Set(markets.map(m => m.state).filter(Boolean))
    return [...s].sort()
  }, [markets])

  const filteredMarkets = useMemo(() => {
    const q = search.trim().toUpperCase()
    return [...markets]
      .filter(m => {
        if (stateFilter !== 'all' && m.state !== stateFilter) return false
        if (dealsOnly && !(entriesMap[m.id] || []).some(e => e.deal)) return false
        if (q) {
          const inName = (m.market || '').toUpperCase().includes(q)
          const inState = (m.state || '').toUpperCase() === q
          const inCalls = (entriesMap[m.id] || []).some(e => (e.call_sign || '').toUpperCase().includes(q))
          if (!inName && !inState && !inCalls) return false
        }
        return true
      })
      .sort((a, b) => (b.share_sum ?? 0) - (a.share_sum ?? 0))
  }, [markets, entriesMap, search, stateFilter, dealsOnly])

  const handleExport = async () => {
    setExporting(true)
    await exportStationShareToExcel(markets, entriesMap, notesMap, stations)
    setExporting(false)
  }

  const isLoading = loadingMarkets || loadingEntries || loadingNotes
  const isFetchingAny = fetchingMarkets || fetchingEntries || fetchingNotes

  if (marketsError) {
    return <BplQueryError label="station share markets" error={marketsErr} onRetry={refetchMarkets} />
  }

  // Show spinner on first load (no cached data yet) or during a background refetch
  // when there is no data to display — prevents empty state flashing during refetch
  if (isLoading || (isFetchingAny && markets.length === 0)) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (markets.length === 0) {
    return (
      <div className="py-20 text-center text-muted-foreground">
        <p className="text-sm">No markets loaded yet.</p>
        <p className="text-xs mt-1">Station share data has not been added yet.</p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Metric Cards */}
      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Markets" value={metrics.markets} />
        <MetricCard label="Stations" value={metrics.stations} />
        <MetricCard
          label="Deal Markets"
          value={metrics.deal}
          highlight={metrics.deal > 0}
          highlightColor="emerald"
          onClick={() => setDealsOnly(v => !v)}
          active={dealsOnly}
        />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search market, state, or call sign…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="w-32 h-9 text-sm">
            <SelectValue placeholder="State" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All States</SelectItem>
            {stateOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {(search || stateFilter !== 'all') && (
          <span className="text-xs text-muted-foreground">
            {filteredMarkets.length.toLocaleString()} result{filteredMarkets.length !== 1 ? 's' : ''}
          </span>
        )}
        <Button
          variant="outline" size="sm"
          className="h-9 gap-1.5 text-xs ml-auto"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Export Excel
        </Button>
      </div>

      {/* Market cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filteredMarkets.map(market => (
          <StationShareCard
            key={market.id}
            market={market}
            entries={entriesMap[market.id] || []}
            notes={notesMap[market.id] || []}
            canWrite={canWrite}
            userName={userName}
          />
        ))}
      </div>

      {filteredMarkets.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-10">No markets match your filters.</p>
      )}
    </div>
  )
}
